import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { CookieConsentProductResult, CookieConsentState } from '../types/cookieConsent';
import { initialiseCookieConsent, hasPolicyConsent } from '../services/cookieConsentService';

const CookieConsentContext = createContext<CookieConsentState>({
  isResolved: false,
  consent: null,
  hasAnalyticsConsent: false,
  hasFunctionalConsent: false,
  hasMarketingConsent: false,
});

export function useCookieConsent(): CookieConsentState {
  return useContext(CookieConsentContext);
}

interface CookieConsentProviderProps {
  children: ReactNode;
}

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const [consent, setConsent] = useState<CookieConsentProductResult | null>(null);
  const [isResolved, setIsResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const result = await initialiseCookieConsent();
      if (!cancelled) {
        setConsent(result);
        setIsResolved(true);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  const state: CookieConsentState = {
    isResolved,
    consent,
    hasAnalyticsConsent: hasPolicyConsent(consent, 'analytics'),
    hasFunctionalConsent: hasPolicyConsent(consent, 'functional'),
    hasMarketingConsent: hasPolicyConsent(consent, 'marketing'),
  };

  return (
    <CookieConsentContext.Provider value={state}>
      {children}
    </CookieConsentContext.Provider>
  );
}
